/* eslint-disable no-template-curly-in-string */

import { PatientFormNormalized } from 'EntityTypes';
import { PatientFormType } from 'constants/patientForms';
import {
  PatientFormsExportLocation,
  PatientFormsNoteExportLocation,
} from 'constants/PatientFormsExportLocation';

export const PATIENT_FORMS_TEMPLATES_DROPDOWN_NAMES: { [key: string]: string } = {
  blank: 'Blank',
  newPatientMedicalHistoryForm: 'New Patient Medical History Template',
  followUpMedicalHistoryForm: 'Follow-Up Medical History Template',
  structuredDemographicsForm: 'Demographics Template',
  insuranceInformationForm: 'Insurance Information Template',
  covid19ScreeningForm: 'COVID-19 Screening Template',
  medicareAwvForm: 'Medicare AWV Template',
  allTopicsTestForm: 'All Topics Test Form',
};

export const PATIENT_FORMS_TEMPLATES: {
  [key: string]: RecursivePartial<PatientFormNormalized> & {
    form_type: PatientFormType;
    name: string;
    title: string;
  };
} = {
  blank: {
    form_type: PatientFormType.MedicalHistory,
    name: '(Blank Form)',
    title: '(Blank Form)',
    topics: [],
  },
  newPatientMedicalHistoryForm: {
    form_type: PatientFormType.MedicalHistory,
    name: 'New Patient Medical History Form',
    title: 'Medical History Form',
    prompt: 'Please help us understand your medical history.',
    topics: [
      {
        concept: 'other',
        questions: [
          {
            prompt: 'What is the reason for your visit?',
            input: {
              placeholder_text: 'e.g. sore throat, annual checkup',
            },
          },
        ],
      },
      { concept: 'caffeine_use' },
      { concept: 'smoking_status' },
      { concept: 'alcohol_use' },
      { concept: 'drug_use' },
      { concept: 'exercise' },
      { concept: 'diet' },
      { concept: 'sleep' },
      { concept: 'family_history' },
      { concept: 'medical_history' },
      { concept: 'allergies' },
      { concept: 'medications' },
      { concept: 'surgical_history' },
      { concept: 'occupation' },
      { concept: 'place_of_birth' },
      { concept: 'colon_cancer_screening' },
      { concept: 'breast_cancer_screening' },
      { concept: 'depression_phq_2' },
      {
        concept: 'other',
        questions: [
          {
            prompt: 'Anything else you would like us to know?',
            input: {
              placeholder_text: '',
            },
          },
        ],
      },
    ],
  },
  followUpMedicalHistoryForm: {
    form_type: PatientFormType.MedicalHistory,
    name: 'Follow-Up Medical History Form',
    title: 'Medical History Form',
    prompt: 'Please help us understand your medical history.',
    topics: [
      {
        concept: 'other',
        questions: [
          {
            prompt: 'What is the reason for your visit?',
            input: {
              placeholder_text: 'e.g. sore throat, annual checkup',
            },
          },
        ],
      },
      { concept: 'exercise' },
      { concept: 'diet' },
      { concept: 'sleep' },
      { concept: 'medications' },
      {
        concept: 'other',
        questions: [
          {
            prompt: 'Any changes in your health since the last visit?',
            input: {
              placeholder_text: '',
            },
          },
        ],
      },
      {
        concept: 'other',
        questions: [
          {
            prompt: 'Anything else you would like us to know?',
            input: {
              placeholder_text: '',
            },
          },
        ],
      },
    ],
  },
  structuredDemographicsForm: {
    form_type: PatientFormType.Demographics,
    name: 'Demographics Form',
    title: 'Patient Information',
    prompt:
      'For any question, if there have been no changes since your last visit, feel free to leave the answer blank. If this is your first visit with us, please answer all questions.',
    topics: [
      { concept: 'basic_demographics' },
      { concept: 'full_name' },
      { concept: 'personal_pronouns' },
      { concept: 'gender_identity' },
      { concept: 'sexual_orientation' },
      { concept: 'contact_methods' },
      { concept: 'patient_address' },
      { concept: 'emergency_contact' },
      { concept: 'preferred_language' },
      { concept: 'race_ethnicity' },
    ],
  },
  insuranceInformationForm: {
    form_type: PatientFormType.Demographics,
    name: 'Insurance Information Form',
    title: 'Insurance Information',
    prompt:
      'For any question, if there have been no changes since your last visit, feel free to leave the answer blank. If this is your first visit with us, please answer all questions.',
    topics: [
      {
        concept: 'other',
        questions: [
          {
            prompt:
              "What is your primary insurance policy? (Policyholder's name, date of birth, and relationship to you; Member ID number; Rx BIN, PCN, and Group ID or Rx Group if applicable)",
            input: {
              placeholder_text:
                'Ex. John Smith, 01/01/1980, Husband, Blue Cross Blue Shield of Arizona, Member 123456, Group 123456',
            },
          },
        ],
      },
      {
        concept: 'other',
        questions: [
          {
            prompt:
              "What is your secondary insurance policy? (Policyholder's name, date of birth, and relationship to you; Member ID number; Rx BIN, PCN, and Group ID or Rx Group if applicable)",
            input: {
              placeholder_text:
                'Ex. John Smith, 01/01/1980, Husband, Blue Cross Blue Shield of Arizona, Member 123456, Group 123456',
            },
          },
        ],
      },
    ],
  },
  covid19ScreeningForm: {
    form_type: PatientFormType.MedicalHistory,
    name: 'COVID-19 Screening Form',
    title: 'COVID-19 Screening',
    prompt:
      "We're going to ask you a few questions according to CDC guidelines to determine your risk for novel coronavirus (COVID-19).",
    topics: [
      {
        concept: 'other',
        questions: [
          {
            prompt:
              'In the past 14 days, have you had contact with anyone known to test positive for Coronavirus, COVID-19?',
            input: {
              placeholder_text: '',
            },
          },
        ],
      },
      {
        concept: 'other',
        questions: [
          {
            prompt:
              'Are you a healthcare worker who has been within 6 feet of a patient suspected of COVID-19 infection or have an occupation where you are in close contact with large numbers of people each day? (example: Health care Facility, Retail Store, Public Transit, Food Industry or Airports)',
            input: {
              placeholder_text: '',
            },
          },
        ],
      },
      {
        concept: 'other',
        questions: [
          {
            prompt:
              'Do you live with, or frequently interact with people who are older than 60 years of age or who have chronic medical conditions such as cancer, heart disease, COPD, asthma, or immune disorders?',
            input: {
              placeholder_text: '',
            },
          },
        ],
      },
      {
        concept: 'other',
        questions: [
          {
            prompt:
              'Which of the following symptoms do you currently have - Fever, Cough, Shortness of breath? (List all that apply)',
            input: {
              placeholder_text: '',
            },
          },
        ],
      },
      {
        concept: 'other',
        questions: [
          {
            prompt:
              'Are you having any of these additional symptoms - Loss of smell, sore throat, fatigue, loss of taste, sore muscles, runny nose, headache, diarrhea? (List all that apply)',
            input: {
              placeholder_text: '',
            },
          },
        ],
      },
      {
        concept: 'other',
        questions: [
          {
            prompt:
              'Do you have any underlying chronic medical conditions - Cancer, heart disease, ESRD, liver disease, history of immune disorder, chronic lung disease, immunosuppression, asthma, COPD, diabetes, pregnancy, hypertension? (List all that apply)',
            input: {
              placeholder_text: '',
            },
          },
        ],
      },
      {
        concept: 'other',
        questions: [
          {
            prompt: 'Did you get tested for COVID-19 anywhere else? If yes, what was the result?',
            input: {
              placeholder_text: '',
            },
          },
        ],
      },
      {
        concept: 'other',
        questions: [
          {
            prompt: 'Please provide your current zip code.',
            input: {
              placeholder_text: '',
            },
          },
        ],
      },
    ],
  },
  medicareAwvForm: {
    form_type: PatientFormType.MedicalHistory,
    name: 'Medicare AWV Health Risk Assessment',
    title: 'Medicare AWV Health Risk Assessment',
    prompt:
      'Welcome to your Medicare Annual Wellness Visit Health Risk Assessment. This questionnaire is designed to help us gather important information about your health, lifestyle, and medical history. Your responses will assist your healthcare provider in developing a personalized prevention plan to keep you healthy and active.\n\nYour privacy is important to us, and all information you provide will be kept confidential and used solely for your healthcare planning.',
    allow_staff_signoff: false,
    topics: [
      {
        concept: 'custom',
        label: 'General Health Assessment',
        default_sequence: 0,
        exports_to_profile_section: PatientFormsExportLocation.HPI,
        can_modify_export_location: true,
        can_skip_explicitly: false,
        form_type: PatientFormType.MedicalHistory,
        intro_text:
          'This section helps us understand your overall health status and recent healthcare experiences.',
        questions: [
          {
            prompt:
              'Considering your age and general health condition, how would you rate your overall health?',
            can_edit_prompt: true,
            export_format: 'Overall health: ${overall_health_rating}',
            input: {
              type: 'radio_button_list',
              name: 'overall_health_rating',
              options: [
                { label: 'Excellent', value: 'excellent' },
                { label: 'Good', value: 'good' },
                { label: 'Poor', value: 'poor' },
              ],
            },
          },
          {
            prompt: 'Have you been seen in the emergency room in the past 6 months?',
            can_edit_prompt: true,
            export_format: 'ER visit past 6 months: ${emergency_room_visit}',
            input: {
              type: 'radio_button_list',
              name: 'emergency_room_visit',
              options: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ],
            },
          },
          {
            prompt: 'Have you been hospitalized in the past 6 months?',
            can_edit_prompt: true,
            export_format: 'Hospitalization past 6 months: ${hospitalization}',
            input: {
              type: 'radio_button_list',
              name: 'hospitalization',
              options: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ],
            },
          },
          {
            prompt: 'Approximately when was your last vision exam?',
            can_edit_prompt: true,
            export_format: 'Last vision exam: ${last_vision_exam}',
            input: {
              type: 'free_text',
              name: 'last_vision_exam',
              placeholder_text: 'Please enter a date.',
              can_edit_placeholder_text: true,
            },
          },
          {
            prompt: 'Do you have any problems with your vision?',
            can_edit_prompt: true,
            export_format: 'Vision problems: ${vision_problems}',
            input: {
              type: 'radio_button_list',
              name: 'vision_problems',
              options: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ],
            },
          },
          {
            prompt: 'Approximately when was your last hearing exam?',
            can_edit_prompt: true,
            export_format: 'Last hearing exam: ${last_hearing_exam}',
            input: {
              type: 'free_text',
              name: 'last_hearing_exam',
              placeholder_text: 'Please enter a date.',
              can_edit_placeholder_text: true,
            },
          },
          {
            prompt: 'Do you have any problems with your hearing?',
            can_edit_prompt: true,
            export_format: 'Hearing problems: ${hearing_problems}',
            input: {
              type: 'radio_button_list',
              name: 'hearing_problems',
              options: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ],
            },
          },
          {
            prompt: 'Approximately when was your last dental exam?',
            can_edit_prompt: true,
            export_format: 'Last dental exam: ${last_dental_exam}',
            input: {
              type: 'free_text',
              name: 'last_dental_exam',
              placeholder_text: 'Please enter a date.',
              can_edit_placeholder_text: true,
            },
          },
          {
            prompt: 'Do you have any problems with your teeth or dentures?',
            can_edit_prompt: true,
            export_format: 'Dental problems: ${dental_problems}',
            input: {
              type: 'radio_button_list',
              name: 'dental_problems',
              options: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ],
            },
          },
        ],
      },
      {
        concept: 'custom',
        label: 'Falls Assessment',
        default_sequence: 1,
        exports_to_profile_section: PatientFormsExportLocation.Functional,
        can_modify_export_location: true,
        can_skip_explicitly: false,
        form_type: PatientFormType.MedicalHistory,
        questions: [
          {
            prompt: 'Have you fallen in the past year?',
            can_edit_prompt: true,
            export_format: 'Fallen past year: ${fallen_past_year}',
            input: {
              type: 'radio_button_list',
              name: 'fallen_past_year',
              options: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ],
            },
          },
          {
            prompt: 'Do you sometimes feel unsteady when you are walking?',
            can_edit_prompt: true,
            export_format: 'Unsteady walking: ${unsteady_walking}',
            input: {
              type: 'radio_button_list',
              name: 'unsteady_walking',
              options: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ],
            },
          },
          {
            prompt: 'Are you worried about falling?',
            can_edit_prompt: true,
            export_format: 'Worried about falling: ${worried_about_falling}',
            input: {
              type: 'radio_button_list',
              name: 'worried_about_falling',
              options: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ],
            },
          },
        ],
      },
      {
        concept: 'custom',
        label: 'Cognitive Assessment',
        default_sequence: 2,
        exports_to_profile_section: PatientFormsExportLocation.Cognitive,
        can_modify_export_location: true,
        can_skip_explicitly: false,
        form_type: PatientFormType.MedicalHistory,
        questions: [
          {
            prompt:
              'Do you or your family members have any concerns about your memory or thinking abilities?',
            export_format: 'Memory concerns: ${memory_concerns}',
            can_edit_prompt: true,
            input: {
              type: 'radio_button_list',
              name: 'memory_concerns',
              options: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ],
            },
          },
        ],
      },
      {
        concept: 'custom',
        label: 'Advance Care Planning',
        default_sequence: 3,
        exports_to_profile_section: PatientFormsExportLocation.Maintenance,
        exports_to_note_section: PatientFormsNoteExportLocation.Psych,
        exports_to_tooltip:
          "In the patient's chart, this will be exportable to the Health Maintenance section in the Clinical Profile, and to the Data section of the Visit Note.",

        can_modify_export_location: true,
        can_skip_explicitly: false,
        form_type: PatientFormType.MedicalHistory,
        questions: [
          {
            export_format: 'Advance Care Plan: ${advance_directive}',
            prompt:
              'Do you have an Advance Directive (Living Will, Durable Power of Attorney for Healthcare)?',
            can_edit_prompt: true,
            input: {
              type: 'radio_button_list',
              name: 'advance_directive',
              options: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ],
            },
          },
        ],
      },
      {
        concept: 'custom',
        label: 'Diet Assessment',
        default_sequence: 4,
        exports_to_profile_section: PatientFormsExportLocation.Diet,
        can_modify_export_location: true,
        can_skip_explicitly: false,
        form_type: PatientFormType.MedicalHistory,
        questions: [
          {
            export_format: 'Balanced diet: ${balanced_diet}',
            prompt:
              'Do you eat a well balanced diet, including protein, high fiber, fruits and vegetables?',
            can_edit_prompt: true,
            input: {
              type: 'radio_button_list',
              name: 'balanced_diet',
              options: [
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ],
            },
          },
        ],
      },
      {
        concept: 'custom',
        label: 'Functional Assessment',
        default_sequence: 5,
        exports_to_profile_section: PatientFormsExportLocation.Functional,
        can_modify_export_location: true,
        can_skip_explicitly: false,
        form_type: PatientFormType.MedicalHistory,
        questions: [
          {
            export_format:
              'IADLs - Functional assistance needed: ${assistance_type} - ${functional_assistance_nested_input}',
            prompt: 'Do you need assistance with any of the following?',
            can_edit_prompt: true,
            input: {
              type: 'checkbox_list',
              name: 'functional_assistance',
              label_text: 'Assistance Need',
              label_name: 'assistance_type',
              label_max_length: 50,
              default_labels: [
                'Managing Medications',
                'Managing Finances',
                'Shopping',
                'Meal Preparation',
                'Housekeeping',
                'Transportation',
                'None',
              ],
              can_add_labels: true,
              list_item_nested_input: {
                type: 'free_text',
                name: 'functional_assistance_nested_input',
                max_length: 100,
                placeholder_text: 'Please describe the assistance needed.',
                can_edit_placeholder_text: true,
              },
            },
          },
        ],
      },
      {
        concept: 'el8_katzadl',
        // explicitly set export info for now
        // TODO: update the template when we do the migration
        exports_to_profile_section: PatientFormsExportLocation.Functional,
        exports_to_label: 'Functional',
        exports_to_tooltip:
          "In the patient's chart, this will be exportable to the Functional section in the Clinical Profile, and to the Subjective section of the Visit Note.",
        default_sequence: 6,
        intro_text:
          'Please respond to the following questions about your ability to bathe, dress, go to the toilet, and perform other activities of daily living.',
      },
      {
        concept: 'el8_ahc_hrsn',
        default_sequence: 7,
        intro_text: 'Please respond to the following questions about health-related social needs.',
      },
    ],
  },
  allTopicsTestForm: {
    form_type: PatientFormType.MedicalHistory,
    name: 'All Topics Test Form',
    title: 'Comprehensive Test Form - All Predefined Topics',
    prompt: 'This form includes every predefined topic for comprehensive testing purposes.',
    topics: [
      // Basic Medical History Topics
      { concept: 'caffeine_use' },
      { concept: 'tobacco_use' },
      { concept: 'alcohol_use' },
      { concept: 'drug_use' },
      { concept: 'exercise' },
      { concept: 'diet' },
      { concept: 'sleep' },
      { concept: 'family_history' },
      { concept: 'medical_history' },
      { concept: 'allergies' },
      { concept: 'medications' },
      { concept: 'surgical_history' },
      { concept: 'occupation' },
      { concept: 'place_of_birth' },
      { concept: 'colon_cancer_screening' },
      { concept: 'breast_cancer_screening' },
      { concept: 'other' },

      // Clinical Form Concepts
      { concept: 'depression_phq_9' },
      { concept: 'depression_phq_2' },
      { concept: 'depression_phq_4' },
      { concept: 'depression_gad_7' },
      { concept: 'alcohol_audit_c' },
      { concept: 'social_connection_index' },
      { concept: 'violence_hark' },
      { concept: 'el8_katzadl' },
      { concept: 'el8_minicog' },
      { concept: '70221-7' }, // PCL-5
      { concept: 'el8_ama_caregiver' },
      { concept: 'el8_pmq9' },
      { concept: '72107-6' }, // MMSE
      { concept: 'el8_whodas_2' },
      { concept: 'el8_panss6' },
      { concept: 'el8_nida' },
      { concept: 'el8_moca' },
      { concept: 'el8_ybocs' },
      { concept: 'el8_ahc_hrsn' },
      { concept: '99549-8' }, // Health Leads Screening Panel
      { concept: '71354-5' }, // EPDS

      // Insurance Card Topics
      { concept: 'primary_insurance_card_images' },
      { concept: 'secondary_insurance_card_images' },
      { concept: 'tertiary_insurance_card_images' },

      // Signature Topic
      { concept: 'signature' },

      // Additional Topics
      { concept: 'smoking_status' },
      { concept: 'patient_profile_photo' },
      { concept: 'other_image_upload' },
    ],
  },
};
